import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button, Group, Modal, Text, UnstyledButton } from '@mantine/core';
import { IconArrowNarrowLeft } from '@tabler/icons-react';
import { type EditorRef, EmailEditor, type EmailEditorProps } from 'react-email-editor';
import { createDesignFromHtml, extractEmailEditorDesign, splitInlineImagesFromDesign } from '@/helpers';
import {
  type EmailEditorContent,
  type EmailEditorContentValues,
  type EmailEditorDesign,
  type EmailEditorExportData,
  type EmailEditorSocialIcon,
} from '@/types/emailEditorDesign';
import styles from './MessageDesigner.module.sass';

const DEFAULT_SOCIAL_THEME = 'circle';

const tools = {
  image: {
    properties: {
      src: {
        editor: {
          widgetParams: {
            shouldRender: {
              editImageButton: false,
            },
          },
        },
      },
    },
  },
  social: {
    enabled: true,
    properties: {
      theme: { value: DEFAULT_SOCIAL_THEME },
      iconType: { value: DEFAULT_SOCIAL_THEME },
      icons: {
        value: {
          editor: { data: { showDefaultIcons: true } },
          icons: [
            { name: 'Facebook', url: 'https://facebook.com/' },
            { name: 'Twitter', url: 'https://twitter.com/' },
            { name: 'Instagram', url: 'https://instagram.com/' },
          ],
          iconType: DEFAULT_SOCIAL_THEME,
        },
      },
    },
    values: {
      iconType: DEFAULT_SOCIAL_THEME,
      theme: DEFAULT_SOCIAL_THEME,
    },
  },
  timer: { enabled: true },
  video: { enabled: true },
};

const decorateSocialIcons = (icons: EmailEditorSocialIcon[], theme = DEFAULT_SOCIAL_THEME): EmailEditorSocialIcon[] =>
  icons.map((icon) => ({
    ...icon,
    iconType: icon.iconType || theme,
    theme: icon.theme || theme,
    icon_theme: icon.icon_theme || theme,
  }));

const sanitizeDesign = (sourceDesign: EmailEditorDesign): EmailEditorDesign => {
  if (!sourceDesign) return sourceDesign;

  let design: EmailEditorDesign;
  try {
    design = JSON.parse(JSON.stringify(sourceDesign)) as EmailEditorDesign;
  } catch {
    return sourceDesign;
  }

  const applySocialDefaults = (values: EmailEditorContentValues) => {
    if (!values || typeof values !== 'object') return;
    const currentTheme = values.theme || values.iconType || DEFAULT_SOCIAL_THEME;
    values.theme = currentTheme;
    values.iconType = currentTheme;
    if (Array.isArray(values.icons)) {
      values.icons = decorateSocialIcons(values.icons, currentTheme);
    }
  };

  const ensureSocialTheme = (contents: EmailEditorContent[]) => {
    if (!Array.isArray(contents)) return;
    contents.forEach((content) => {
      if (!content || typeof content !== 'object') return;
      if (content.type === 'social') {
        content.values = content.values || {};
        applySocialDefaults(content.values);
      }
      if (Array.isArray(content.columns)) {
        content.columns.forEach((column) => {
          if (column && Array.isArray(column.contents)) ensureSocialTheme(column.contents);
        });
      }
    });
  };

  if (design.body && Array.isArray(design.body.rows)) {
    design.body.rows.forEach((row) => {
      if (row && Array.isArray(row.columns)) {
        row.columns.forEach((column) => {
          if (column && Array.isArray(column.contents)) ensureSocialTheme(column.contents);
        });
      }
    });
  }

  return design;
};

const buildBlankDesign = (): EmailEditorDesign => ({
  body: {
    rows: [{ cells: [1], columns: [{ contents: [] }] }],
    values: { backgroundColor: '#ffffff' },
  },
});

const resolveDesignForEditor = ({
  editorDesign,
  messageText,
}: {
  editorDesign?: EmailEditorDesign | null;
  messageText?: string;
}): EmailEditorDesign => {
  const embedded = extractEmailEditorDesign({ html: messageText ?? '' });
  const design = editorDesign ?? embedded.design;

  if (design) {
    return sanitizeDesign(splitInlineImagesFromDesign({ design }));
  }

  if (embedded.html.trim()) {
    return sanitizeDesign(createDesignFromHtml({ html: embedded.html }));
  }

  return sanitizeDesign(buildBlankDesign());
};

type MessageDesignerProps = {
  editorDesign?: EmailEditorDesign | null;
  messageText?: string;
  onSave: (data: EmailEditorExportData) => void;
  onCancel: () => void;
};

export const MessageDesigner = ({ editorDesign, messageText, onSave, onCancel }: MessageDesignerProps) => {
  const emailEditorRef = useRef<EditorRef>(null);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const closeConfirmationModal = useCallback(() => {
    setIsDiscardModalOpen(false);
  }, []);

  const options = useMemo(
    () => ({
      projectId: 14988,
      tools,
      mergeTags: {
        first_name: { name: 'First Name', value: '{{first_name}}', sample: 'John' },
        last_name: { name: 'Last Name', value: '{{last_name}}', sample: 'Smith' },
        birth_date: { name: 'Birthdate', value: '{{birth_date}}', sample: 'January 1, 1990' },
        phone_number: { name: 'Phone Number', value: '{{phone_number}}', sample: '(555) 555-5555' },
      },
      features: {
        pageAnchors: true,
        imageEditor: {
          enabled: false,
        },
        stockImages: {
          enabled: true as const,
          safeSearch: true as const,
          defaultSearchTerm: 'christian',
        },
        textEditor: {
          cleanPaste: false,
          inlineFontControls: true,
        },
      },
    }),
    []
  );

  const exportHtml = () => {
    const unlayer = emailEditorRef.current?.editor;
    unlayer?.exportHtml((data) => {
      const { html, design } = data;
      onSave({ html: html || '', design: design as EmailEditorDesign });
    });
  };

  const handleBackButtonClick = () => {
    setIsDiscardModalOpen(true);
  };

  const handleLeaveWithoutSaving = () => {
    setIsDiscardModalOpen(false);
    onCancel();
  };

  const loadDesignIntoEditor = () => {
    if (!emailEditorRef.current?.editor) return;
    const editor = emailEditorRef.current.editor;

    editor.loadDesign(resolveDesignForEditor({ editorDesign, messageText }) as never);
  };

  const handleEditorLoad: EmailEditorProps['onReady'] = () => {
    const checkEditor = () => {
      if (!emailEditorRef.current?.editor) {
        setTimeout(checkEditor, 100);
        return;
      }
      loadDesignIntoEditor();
    };
    checkEditor();
  };

  return (
    <>
      <Group justify="space-between" align="center" className={styles.header} p="md">
        <Group gap="xs" align="center">
          <UnstyledButton onClick={handleBackButtonClick} className={styles.backButton}>
            <IconArrowNarrowLeft size={20} />
            <Text size="lg" fw={500}>
              Message Designer
            </Text>
          </UnstyledButton>
        </Group>
        <Group gap="xs">
          <Button variant="filled" onClick={exportHtml}>
            Save and Continue
          </Button>
        </Group>
      </Group>
      <Box className={styles.editorContainer} pos="relative">
        <EmailEditor
          ref={emailEditorRef}
          projectId={14988}
          options={options}
          tools={tools}
          onReady={handleEditorLoad}
        />
      </Box>

      <Modal
        opened={isDiscardModalOpen}
        onClose={closeConfirmationModal}
        title="Discard changes?"
        size="md"
        centered
        zIndex={1100}
      >
        <Text className={styles.discardMessage}>
          You have unsaved changes in the designer. Are you sure you want to leave?
        </Text>
        <Group justify="flex-end" mt="md" gap="xs" wrap="nowrap" className={styles.discardActions}>
          <Button variant="default" size="sm" onClick={closeConfirmationModal}>
            Keep Editing
          </Button>
          <Button variant="filled" color="red" size="sm" onClick={handleLeaveWithoutSaving}>
            Leave Without Saving
          </Button>
        </Group>
      </Modal>
    </>
  );
};
